import { inject, Service, service } from './di'
import { SessionStorageService } from './SessionStorageService'
import { Country, LocationService } from './LocationService'
import { ConfigService } from './ConfigService'
import { DateTimeService } from './DateTimeService'
import { ReleaseService } from './ReleaseService'

interface ActiveBanner {
    id: string
    startDate: Date
    endDate: Date
    countries: Country[]
    type: 'giveaway' | 'normal'
    topBarBanner?: boolean
}

export const ACTIVE_BANNERS: ActiveBanner[] = [
    {
        id: 'proudly_canadian',
        startDate: new Date('May 1, 2025 00:00:01'),
        endDate: new Date('May 31, 2025 23:59:59'),
        countries: [Country.CANADA],
        type: 'normal',
    },
    {
        id: 'canada_day_giveaway',
        startDate: new Date('June 25, 2025 00:00:01'),
        endDate: new Date('June 28, 2025 23:59:59'),
        countries: [Country.CANADA],
        type: 'giveaway',
        topBarBanner: true,
    },
]

@service('BannerService')
export class BannerService extends Service {
    @inject config: ConfigService
    @inject location: LocationService
    @inject dateService: DateTimeService
    @inject releaseService: ReleaseService

    isSmartNews() {
        const sessionStorageService = SessionStorageService.getInstance()
        const synd = sessionStorageService.getItem('synd')
        return synd === 'smartnews'
    }

    shouldDisplayCarousel() {
        return (
            ACTIVE_BANNERS.reduce((acc, banner) => {
                if (this.isBannerVisible(banner)) return acc + 1
                return acc
            }, 0) > 1
        )
    }

    getGiveawayLink() {
        const dateNow = this.dateService.getNowEST()
        const day = dateNow.day
        const month = dateNow.month
        const locale = this.location.locale
        const linksForDay = GIVEAWAY_LINKS[`${day}-${month}`]
        return linksForDay ? linksForDay[locale] : '/'
    }

    isBannerVisible(banner: ActiveBanner): boolean {
        if (banner.type === 'giveaway') {
            const urlParams = new URLSearchParams(window.location.search)
            if (urlParams.has('giveaway')) return true
        }

        return this.hasValidDates(banner)
    }

    hasValidDates(banner: ActiveBanner): boolean {
        const dateNow = this.dateService.getNowEST()
        const dateStart = this.dateService.fromJsDateEST(banner.startDate)
        const dateEnd = this.dateService.fromJsDateEST(banner.endDate)
        return (
            dateNow >= dateStart &&
            dateNow <= dateEnd &&
            banner.countries.indexOf(this.location.country) !== -1
        )
    }

    shouldShowProudlyCanadianBanner() {
        const proudlyCanadianBanner = ACTIVE_BANNERS.find(
            (banner) => banner.id === 'proudly-canadian'
        )
        return (
            proudlyCanadianBanner && this.isBannerVisible(proudlyCanadianBanner)
        )
    }

    getTopBarBanner() {
        return ACTIVE_BANNERS.find(
            (banner) => banner.topBarBanner && this.isBannerVisible(banner)
        )
    }
}

const GIVEAWAY_LINKS: any = {
    '12-5': {
        'en-ca':
            'https://gleam.io/LHtjL/canada-spring-into-summer-giveaway-day-1',
        'fr-ca': 'https://gleam.io/ihFfz/concours-plongez-dans-lete-jour-1',
        'en-us': 'https://gleam.io/MkXzX/us-spring-into-summer-giveaway-day-1',
    },
    '13-5': {
        'en-ca':
            'https://gleam.io/TzuP0/canada-spring-into-summer-giveaway-day-2',
        'fr-ca': 'https://gleam.io/e73qS/concours-plongez-dans-lete-jour-2',
        'en-us': 'https://gleam.io/cHXXG/us-spring-into-summer-giveaway-day-2',
    },
    '14-5': {
        'en-ca':
            'https://gleam.io/OPbS3/canada-spring-into-summer-giveaway-day-3',
        'fr-ca': 'https://gleam.io/pRrkY/concours-plongez-dans-lete-jour-3',
        'en-us': 'https://gleam.io/MDq5a/us-spring-into-summer-giveaway-day-3',
    },
    '15-5': {
        'en-ca':
            'https://gleam.io/0Qlm4/canada-spring-into-summer-giveaway-day-4',
        'fr-ca': 'https://gleam.io/p3Ajv/concours-plongez-dans-lete-jour-4',
        'en-us': 'https://gleam.io/Rny9M/us-spring-into-summer-giveaway-day-4',
    },
    '16-5': {
        'en-ca':
            'https://gleam.io/g6GIz/canada-spring-into-summer-giveaway-day-5',
        'fr-ca': 'https://gleam.io/LiRjh/concours-plongez-dans-lete-jour-5',
        'en-us': 'https://gleam.io/6PasO/us-spring-into-summer-giveaway-day-5',
    },
    '25-6': {
        'en-ca': 'https://gleam.io/wHInG/canada-day-giveaway-day-1',
        'fr-ca': 'https://gleam.io/ny0dw/concours-de-la-fete-du-canada-jour-1',
    },
    '26-6': {
        'en-ca': 'https://gleam.io/09teW/canada-day-giveaway-day-2',
        'fr-ca': 'https://gleam.io/YISWO/concours-de-la-fete-du-canada-jour-2',
    },
    '27-6': {
        'en-ca': 'https://gleam.io/Mj1xQ/canada-day-giveaway-day-3',
        'fr-ca': 'https://gleam.io/BbGpf/concours-de-la-fete-du-canada-jour-3',
    },
    '28-6': {
        'en-ca': 'https://gleam.io/8iGFi/canada-day-giveaway-day-4',
        'fr-ca': 'https://gleam.io/JhFj3/concours-de-la-fete-du-canada-jour-4',
    },
}
